import { useMemo } from 'react';
import { useContent } from 'contexts/Content/Content';
import { getUrl, resolveKromaticSiteUrl, withUtm } from 'helpers';
import {
    Button,
    Container,
    Content,
    Link,
    Links,
    LogoBlack,
    LogoContainer,
    LogoPicture,
    LogoWhite,
    PoweredBy,
    Text,
    Wrap,
} from './Footer.styled';

export default function Footer({ style, visible = true }: FooterProps) {
    const content = useContent();

    const { buttonLabel, buttonLink, links, poweredBy, utmCampaignFeedback } = content.footer;
    const button = !!(buttonLabel && buttonLink);
    const homeLink = useMemo(
        () =>
            withUtm(resolveKromaticSiteUrl('https://kromatic.com'), {
                medium: 'footer',
                campaign: 'brand-home',
            }),
        []
    );
    const resolvedLinks = useMemo(
        () =>
            links.map(link => ({
                ...link,
                href: withUtm(resolveKromaticSiteUrl(link.href), {
                    medium: 'footer',
                    campaign: link.utmCampaign,
                }),
            })),
        [links]
    );

    const feedbackLink = useMemo(() => {
        if (!buttonLink) {
            return '';
        }

        const resolvedButtonLink = withUtm(resolveKromaticSiteUrl(buttonLink), {
            medium: 'cta',
            campaign: utmCampaignFeedback ?? 'feedback',
        });

        if (typeof window === 'undefined') {
            return resolvedButtonLink;
        }

        try {
            const url = new URL(resolvedButtonLink);
            const sourcePath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
            url.searchParams.set('source_url', window.location.href);
            url.searchParams.set('source_path', sourcePath);
            return url.toString();
        } catch (_error) {
            return resolvedButtonLink;
        }
    }, [buttonLink, utmCampaignFeedback]);

    return (
        <Wrap style={style}>
            {visible && (
                <Container>
                    <LogoContainer>
                        {poweredBy && <PoweredBy data-testid="footer-powered-by">Powered by</PoweredBy>}
                        <a
                            aria-label="Kromatic home"
                            data-testid="footer-logo-link"
                            href={homeLink}
                            rel="noreferrer"
                            target="_blank"
                        >
                            <LogoPicture>
                                <LogoBlack alt="Kromatic logo" height={36} src={getUrl('/assets/kromatic-black.svg')} />
                                <LogoWhite alt="Kromatic logo" height={36} src={getUrl('/assets/kromatic-white.svg')} />
                            </LogoPicture>
                        </a>
                    </LogoContainer>
                    <Content>
                        <Text $compact={button}>
                            <div>Copyright © {new Date().getFullYear()} Kromatic</div>
                            <Links>
                                {resolvedLinks.map((link, index) => (
                                    <Link
                                        aria-label={link.label}
                                        data-testid={`footer-link-${index}`}
                                        href={link.href}
                                        key={index}
                                        rel="noreferrer"
                                        target="_blank"
                                    >
                                        {link.label}
                                    </Link>
                                ))}
                            </Links>
                        </Text>
                        {button && (
                            <Button
                                aria-label={buttonLabel}
                                data-testid="footer-feedback"
                                href={feedbackLink}
                                rel="noreferrer"
                                target="_blank"
                            >
                                {buttonLabel}
                            </Button>
                        )}
                    </Content>
                </Container>
            )}
        </Wrap>
    );
}
